import {
  SupplierOptionTuple,
  SupplierProductTuple,
  SupplierTuple,
} from "models/supplier"
import { fetchPlanCrSuppliers, fetchPlanSuppliers } from "api/suppliers"
//
import { queryCache, useQuery } from "react-query"

import { CellProps } from "components/TreeState"
import { ChangedPlanRecord } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { ListCell } from "./ListCell"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { get } from "lodash"
import set from "lodash/fp/set"
import { useIsEditableCrOrRevision } from "../useIsEditableCrOrRevision"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"

const TUPLE_ID_SEPARATOR = "/"

export function Vendor(props: CellProps) {
  const { id: planId } = useParams<{ id: string }>()
  const { rowId: id, primaryKey, disabled } = props
  const { value: isEditableCr } = useIsEditableCrOrRevision()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const {
    type,
    mediaId,
    ancestorMediaSemanticCode,
    ancestorMediaId,
    packaged,
    packageId,
  } = readFrom[id] || {}

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const mediaTypeSemanticCode =
    !!packaged && type === "line_item"
      ? get(readFrom[packageId || ""], "ancestorMediaSemanticCode")
      : ancestorMediaSemanticCode

  const ps = new URLSearchParams()
  !!mediaTypeSemanticCode &&
    ps.set("mediaTypeSemanticCode", mediaTypeSemanticCode)

  const mtps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    mtps.toString(),
  ])

  const mediaTypeId = type === "media_type_plan" ? mediaId : ancestorMediaId

  const defaultFieldIds = mediaTypes?.data?.find(
    (mt) => mt.id === mediaTypeId,
  )?.defaultFieldIdBySemantic

  const { vendor: vendorFieldId, vendorProduct: vendorProductFieldId } =
    defaultFieldIds || {}

  const vendorTuple =
    !!packaged && type === "line_item"
      ? (get(
          readFrom[packageId || ""],
          `${primaryKey}.${vendorFieldId}`,
        ) as SupplierTuple)
      : (get(readFrom[id], `${primaryKey}.${vendorFieldId}`) as SupplierTuple)

  const selected = !!vendorTuple ? vendorTupleToString(vendorTuple) : undefined

  // vendors lookups for draft status
  const draftCached = queryCache.getQueryData<Paginated<SupplierOptionTuple>>([
    "plan-media-vendors",
    planId,
    ps.toString(),
  ])
  const { data: draftVendorTuples } = useQuery(
    ["plan-media-vendors", planId, ps.toString()],
    fetchPlanSuppliers,
    {
      enabled: !!mediaTypeSemanticCode && !draftCached,
      initialData: draftCached,
      refetchOnMount: !draftCached,
    },
  )

  // vendors lookups for CR
  const activeVendorProduct = get(
    readFrom[id],
    `${primaryKey}.${vendorProductFieldId}`,
  ) as SupplierProductTuple
  const vendorProductId = activeVendorProduct?.supplierProductId

  const crprms = new URLSearchParams()
  !!mediaTypeSemanticCode &&
    crprms.set("mediaTypeSemanticCode", mediaTypeSemanticCode)
  crprms.set(
    "intermediaryId",
    vendorTuple?.intermediaryId || activeVendorProduct?.intermediaryId || "",
  )

  const crCached = queryCache.getQueryData<Array<SupplierOptionTuple>>([
    "plan-media-cr-vendors",
    planId,
    vendorProductId,
    crprms.toString(),
  ])
  const { data: crVendorTuples } = useQuery(
    ["plan-media-cr-vendors", planId, vendorProductId, crprms.toString()],
    fetchPlanCrSuppliers,
    {
      enabled:
        !!mediaTypeSemanticCode &&
        !!vendorProductId &&
        !crCached &&
        isEditableCr,
      initialData: crCached,
      refetchOnMount: !crCached,
    },
  )

  const onChange = (selectedVendorId?: string) => {
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])

    if (!isEditableCr) {
      writeTo[id] = set(
        `${primaryKey}.${vendorProductFieldId}`,
        undefined,
        writeTo[id],
      )
    } else {
      // we need to select the first available vendor product
      const vendor = crVendorTuples?.find((v) => {
        const vto = getOptionValue(v)
        return vto === selectedVendorId
      })

      if (!!vendor) {
        const product = vendor.supplierProducts?.[0]
        if (!!product) {
          const newValue = {
            supplierProductId: product.id,
            mediaOwnerId: vendor.mediaOwner.id,
            intermediaryId: vendor.intermediary?.id,
          }
          writeTo[id] = set(
            `${primaryKey}.${vendorProductFieldId}`,
            newValue,
            writeTo[id],
          )
        }
      }
    }
  }

  const vendorTuples =
    isEditableCr && !!vendorProductId ? crVendorTuples : draftVendorTuples?.data
  const options = vendorTuples?.map(getVendorTupleOption) || []

  const isDisabled =
    !!packaged ||
    disabled ||
    (primaryKey === "buyingMediaFields" && !isEditableCr) ||
    (type === "package" && isEditableCr) ||
    buyingStatus === "POSTED_FOR_BILLING"

  return (
    <ListCell
      {...props}
      bulkDisabled={true}
      options={options}
      onChange={onChange}
      selected={selected}
      normalize={normalize}
      disabled={isDisabled}
      dataKey={`${primaryKey}.${vendorFieldId}`}
    />
  )
}

export function vendorTupleToString(vt: SupplierTuple) {
  const value = vt.intermediaryId
    ? [vt.mediaOwnerId, vt.intermediaryId].join(TUPLE_ID_SEPARATOR)
    : vt.mediaOwnerId

  return value
}

function getOptionValue(vt: SupplierOptionTuple) {
  const mediaOwnerId = vt.mediaOwner.id
  const intermediaryId = vt.intermediary?.id
  const value = intermediaryId
    ? [mediaOwnerId, intermediaryId].join(TUPLE_ID_SEPARATOR)
    : mediaOwnerId

  return value
}

function getOptionLabel(vt: SupplierOptionTuple) {
  return `${vt.mediaOwner.name}${
    vt?.intermediary ? `/ ${vt.intermediary.name}` : ""
  }`
}

export function getVendorTupleOption(vt: SupplierOptionTuple) {
  return { label: getOptionLabel(vt), value: getOptionValue(vt) }
}

function normalize(stringId: string): SupplierTuple {
  const isTuple = stringId.indexOf(TUPLE_ID_SEPARATOR) > -1
  if (!isTuple) return { mediaOwnerId: stringId }

  const [mediaOwnerId, intermediaryId] = stringId.split(TUPLE_ID_SEPARATOR)
  return { mediaOwnerId, intermediaryId }
}
